import React, { useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';

import { LucideIcon, MoreVertical } from 'lucide-react';
import AppTooltip from '../AppToolTip';

export type ActionTone = 'default' | 'success' | 'warning' | 'danger';

export interface ActionMenuItem {
  label: string;
  onClick: () => void;
  icon?: LucideIcon;
  tone?: ActionTone;
  disabled?: boolean;
  tooltip?: string;
}

interface ActionMenuProps {
  items: ActionMenuItem[];
  showIcons?: boolean;
  useTone?: boolean;
  className?: string;
  buttonClassName?: string;
  menuClassName?: string;
  iconClassName?: string;
}

const toneClasses: Record<ActionTone, string> = {
  default: 'text-neutral-700 dark:text-neutral-300',
  success: 'text-success-600 dark:text-success-400',
  warning: 'text-warning-600 dark:text-warning-400',
  danger: 'text-error-600 dark:text-error-400',
};

export const ActionMenu: React.FC<ActionMenuProps> = ({
  items,
  showIcons = true,
  useTone = true,
  className = '',
  buttonClassName = '',
  menuClassName = '',
  iconClassName = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const menuRef = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);

  const updateCoords = useCallback(() => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const menuWidth = 160; // Default width
      setCoords({
        top: rect.bottom + window.scrollY,
        left: Math.max(0, rect.right - menuWidth + window.scrollX),
      });
    }
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    const handleScrollOrResize = () => {
      if (isOpen) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      updateCoords();
      document.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('scroll', handleScrollOrResize, true);
      window.addEventListener('resize', handleScrollOrResize);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScrollOrResize, true);
      window.removeEventListener('resize', handleScrollOrResize);
    };
  }, [isOpen, updateCoords]);

  if (!items.length) {
    return null;
  }

  return (
    <div className={`relative ${className}`}>
      <button
        ref={buttonRef}
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          setIsOpen((prev) => !prev);
        }}
        className={`p-1 hover:bg-neutral-100 dark:hover:bg-neutral-900 rounded transition-colors cursor-pointer ${buttonClassName}`.trim()}
        aria-haspopup="menu"
        aria-expanded={isOpen}
      >
        <MoreVertical
          className={`w-4 h-4 text-neutral-500 ${iconClassName}`.trim()}
        />
      </button>

      {isOpen &&
        createPortal(
          <div
            ref={menuRef}
            style={{
              position: 'absolute',
              top: coords.top + 4,
              left: coords.left,
              width: '160px',
            }}
            className={`bg-white dark:bg-neutral-950 border border-neutral-200 dark:border-neutral-800 rounded-lg shadow-lg py-1 z-[9999] ${menuClassName}`.trim()}
          >
            {items.map((item) => {
              const toneClass = useTone
                ? toneClasses[item.tone ?? 'default']
                : toneClasses.default;
              const ItemIcon = item.icon;

              const button = (
                <button
                  key={item.label}
                  type="button"
                  disabled={item.disabled}
                  onClick={(event) => {
                    event.stopPropagation();
                    if (!item.disabled) {
                      item.onClick();
                      setIsOpen(false);
                    }
                  }}
                  className={`w-full px-3 py-2 text-left text-sm transition-colors flex items-center gap-2 ${
                    item.disabled
                      ? 'opacity-50 cursor-not-allowed pointer-events-auto'
                      : 'hover:bg-neutral-50 dark:hover:bg-neutral-900 cursor-pointer'
                  } ${toneClass}`.trim()}
                >
                  {showIcons && ItemIcon && <ItemIcon className="w-4 h-4" />}
                  {item.label}
                </button>
              );

              return item.tooltip ? (
                <AppTooltip key={item.label} title={item.tooltip}>
                  {button}
                </AppTooltip>
              ) : (
                <React.Fragment key={item.label}>{button}</React.Fragment>
              );
            })}
          </div>,
          document.body
        )}
    </div>
  );
};
